// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 12-azure-installation.md

import * as React from 'react';

import {
  INSTALL_STEPS,
  installStepApplies,
  type BillingProviderType,
  type InstallStep,
} from '@greenlight/shared';

/**
 * The wizard's screens. Grouping is presentation only — every server step id in
 * `steps` keeps its own persisted state and gating; a group with several ids is
 * one screen that drives all of them (docs/28 §/install makes screen count an
 * implementation choice).
 */
export interface InstallStepGroup {
  id: InstallStepGroupId;
  label: string;
  steps: readonly InstallStep[];
}

export type InstallStepGroupId =
  | 'organization'
  | 'dns_https'
  | 'idp'
  | 'github_app'
  | 'plugin_marketplace'
  | 'payment'
  | 'summary';

export const INSTALL_STEP_GROUPS: readonly InstallStepGroup[] = [
  { id: 'organization', label: 'Organization', steps: ['org_setup', 'contact', 'infrastructure'] },
  {
    id: 'dns_https',
    label: 'DNS & HTTPS',
    steps: ['dns', 'control_plane_https', 'verified_url_handoff'],
  },
  { id: 'idp', label: 'Identity provider', steps: ['idp'] },
  { id: 'github_app', label: 'GitHub App', steps: ['github_app'] },
  { id: 'plugin_marketplace', label: 'Agent plugin', steps: ['plugin_marketplace'] },
  { id: 'payment', label: 'Payment', steps: ['payment'] },
  { id: 'summary', label: 'Finish', steps: ['summary'] },
];

/** The screens this install shows: a step that does not apply to it is never offered. */
export function stepGroupsFor(billingProvider: BillingProviderType): readonly InstallStepGroup[] {
  return INSTALL_STEP_GROUPS.filter((group) =>
    group.steps.some((step) => installStepApplies(step, billingProvider)),
  );
}

/** The screens the current wizard shows, so a step's "Step N of M" counts only those. */
export const InstallStepGroupsContext =
  React.createContext<readonly InstallStepGroup[]>(INSTALL_STEP_GROUPS);

/** Every server step belongs to exactly one group — a missing id is a coding error. */
export function groupForStep(step: InstallStep): InstallStepGroup {
  const group = INSTALL_STEP_GROUPS.find((g) => g.steps.includes(step));
  if (!group) throw new Error(`No install step group contains "${step}".`);
  return group;
}

function groupById(id: InstallStepGroupId): InstallStepGroup {
  const group = INSTALL_STEP_GROUPS.find((g) => g.id === id);
  if (!group) throw new Error(`Unknown install step group "${id}".`);
  return group;
}

/** The "Step N of M" position a step's screen occupies, in group terms. */
export function stepPosition(
  step: InstallStep,
  groups: readonly InstallStepGroup[] = INSTALL_STEP_GROUPS,
): { index: number; count: number } {
  const group = groupForStep(step);
  return { index: groups.indexOf(group) + 1, count: groups.length };
}

/** The group after `id`, or `null` at the end of the wizard. */
export function nextGroupId(
  id: InstallStepGroupId,
  groups: readonly InstallStepGroup[] = INSTALL_STEP_GROUPS,
): InstallStepGroupId | null {
  const next = groups[groups.indexOf(groupById(id)) + 1];
  return next ? next.id : null;
}

/** The last step of `step`'s group — the one whose completion moves the wizard on. */
export function isLastStepOfGroup(step: InstallStep): boolean {
  const { steps } = groupForStep(step);
  return steps[steps.length - 1] === step;
}

/** The first step of the wizard this install shows that is not yet complete. */
export function deriveCurrentStep(
  steps: Partial<Record<InstallStep, { status?: string }>>,
  billingProvider: BillingProviderType = 'direct',
): {
  step: InstallStep;
  groupId: InstallStepGroupId;
} {
  const step =
    INSTALL_STEPS.find(
      (s) => installStepApplies(s, billingProvider) && steps[s]?.status !== 'complete',
    ) ?? 'summary';
  return { step, groupId: groupForStep(step).id };
}
